import { getProxiedServiceUrl } from 'gw-portals-url-js';
import { JsonRPCService } from 'gw-portals-transport-js';

/**
 * Invokes the given method on the backend endpoint passing the given data as parameters
 *
 * @param {any} method the method to invoke on the backend endpoint
 * @param {any} data the data to be passed as part of the invokation on the backend
 * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
 * @returns {Promise} the promise from the backend call
 */
function processSubmission(method, data, additionalHeaders = {}) {
    return JsonRPCService.send(getProxiedServiceUrl('gatewayquote/quote'), method, data, additionalHeaders);
}

/**
 * This is the server responsible for dealing with the Quote and Buy backend operations
 *
 * @export
 * @class LoadSaveService
 */
export default class LoadSaveService {
    /**
     * Updates, saves and performs a quote on an existing submission.
     * Generates quotes for all available product offerings.
     *
     * @param {Object} data tge submission that will be saved
     * @returns {Promise} the promise from the backend call
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @memberof LoadSaveService
     */
    static saveAndQuoteSubmission(data, additionalHeaders = {}) {
        return processSubmission('saveAndQuote', [data], additionalHeaders);
    }

    /**
     * Retrieves a submission.
     *
     * @param {Object} data the retrieval payload (QuoteRetrievalDTO)
     * @returns {Promise} the promise from the backend call
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @memberof LoadSaveService
     */
    static retrieveSubmission(data, additionalHeaders = {}) {
        return processSubmission('retrieve', [data], additionalHeaders);
    }

    /**
     * Updates a quoted submission with LOB Data.
     *
     * @param {Object} data the submission to be saved as draft
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static updateQuotedSubmissionWithLOBData(data, additionalHeaders = {}) {
        return processSubmission('updateQuotedSubmissionWithLOBData', [data], additionalHeaders);
    }

    /**
     *
     * TODO : fix the parameter issues from payment details page
     * Retrieves a submission.
     *
     * @param {string} quoteID quoteID of submission that needs to be found
     * @param {string} sessionUUID current session id
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static retrievePaymentPlans(quoteID, sessionUUID, additionalHeaders = {}) {
        return processSubmission('retrievePaymentPlans', [quoteID, sessionUUID,false], additionalHeaders);
    }

    /**
     * Binds the submission.
     *
     * @param {Object} data the submission to be bound
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static bindSubmission(data, additionalHeaders = {}) {
        return processSubmission('bind', [data], additionalHeaders);
    }

    /**
     * Updates an existing draft submission.
     *
     * @param {Object} data the submission to be saved as draft
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static updateDraftSubmission(data, additionalHeaders = {}) {
        return processSubmission('updateDraftSubmission', [data], additionalHeaders);
    }

    /**
     * Updates a quoted submission.
     *
     * @param {Object} data the submission to be saved as draft
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static updateQuotedSubmission(data, additionalHeaders = {}) {
        return processSubmission('updateQuotedSubmission', [data], additionalHeaders);
    }

    /**
     * Updates coverages.
     *
     * @param {Object} quoteID quoteID
     * @param {Object} sessionUUID sessionUUID
     * @param {Object} lobCoverages the coverages to be saved
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */

    static updateCoverages(quoteID, sessionUUID, lobCoverages, additionalHeaders = {}) {
        return processSubmission(
            'updateCoverages',
            [quoteID, sessionUUID, lobCoverages],
            additionalHeaders
        );
    }

    /**
     * Approve Underwriting Issue.
     *
     * @param {Object} quoteID quoteID
     * @param {Object} uwIssue uwIssue
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static approveUnderwritingIssue(quoteID, uwIssue, additionalHeaders = {}) {
        return processSubmission(
            'approveUnderwritingIssue',
            [quoteID, uwIssue],
            additionalHeaders
        );
    }

    /**
     *
     * @param {Object} data
     * @param {Object} additionalHeaders
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static getPolicyLocations(data, isIssuanceJob, additionalHeaders = {}) {
        return processSubmission(
            'getPolicyLocations',
             [data,isIssuanceJob],
            additionalHeaders
        );
    }
    /**
     *
     * @param {Object} data
     * @param {Object} additionalHeaders
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
static getAccountContacts(data, additionalHeaders = {}) {
        return processSubmission(
            'getAccountContacts',
             [data,false],
            additionalHeaders
        );


    }

    static getLiabilityExtensions(data, isIssuanceJob, additionalHeaders = {}) {
        return processSubmission(
            'getLiabilityExtensions',
             [data,isIssuanceJob],
            additionalHeaders
        );
    }

    static addLiabilityExtenstionForProduct(data, selectedExtensions, additionalHeaders = {}) {
        return processSubmission(
            'addLiabilityExtenstionForProduct',
             [data,selectedExtensions],
            additionalHeaders
        );

    }

    static removeLiabilityExtensionForProduct(data, selectedExtensions, additionalHeaders = {}) {
        return processSubmission(
            'removeiabilityExtenstionForProduct',
             [data,selectedExtensions],
            additionalHeaders
        );
    }

    /**
     * Order MVR call
     *
     * @param {Object} data the submission to be saved as draft
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static orderMVRorAutoPlusForDrivers(data, additionalHeaders = {}) {
        return processSubmission('orderMVRorAutoPlusForDrivers', [data,'OrderMVR'], additionalHeaders);
    }

    static updateQuotedSubmissionWithPaymentData(data,additionalHeaders = {} ) {
        return processSubmission(
            'updateQuotedSubmissionWithPaymentData',
             [data],
            additionalHeaders
        );
    }

    /**
     * Generate Signing Package.
     *
     * @param {Number} submission the submission
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise}
     * @memberof LoadSaveService
     */
     static generateSigningPackageDetails(submission, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayquote/quote'),
            'saveGenerateSigningPackageDetails',
            [submission],
            additionalHeaders
        );
    }

    static generateSigningPackageDocument(quoteID, isIssuanceJob, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayquote/quote'),
            'generateSigningPackageDetailsDocument',
            [quoteID, isIssuanceJob],
            additionalHeaders
        );
    }

    static generateSigningPackageDocumentPortal(quoteID, isIssuanceJob, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayquote/quote'),
            'generateSigningPackageDetailsDocumentPortal',
            [quoteID, isIssuanceJob],
            additionalHeaders
        );
    }

    static createOrUpdatePolicyLocation(submission, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayquote/quote'),
            'createOrUpdatePolicyLocation',
            submission,
            additionalHeaders
        );
    }


    static searchExistingContactRecords(submission, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayquote/quote'),
            'searchExistingContactRecords',
            submission,
            additionalHeaders
        );
    }

    static getCategoryForConviction(convictionCode, additionalHeaders = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('gatewayquote/quote'), 'getCategoryForConviction',
            [convictionCode], additionalHeaders);
    }

    static updateGenderDOBFromLicenseNumber(licenseNumber, licenseState, additionalHeaders = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('gatewayquote/quote'), 'updateGenderDOBFromLicenseNumber',
            [licenseNumber, licenseState], additionalHeaders);
    }

    /* get offerings.
     *
     * @param {Object} quoteID quoteID
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static getOfferings(quoteID,  additionalHeaders = {}) {
        return processSubmission('getOfferings',[quoteID], additionalHeaders);
    }

    /* Update the primary named insured
     *
     * @param {Object} quoteID quoteID
     * @param {Object} contactPublicId  is the Contact Public ID
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static updatePrimaryNamedInsured(quoteID, contactPublicId, additionalHeaders = {}) {
        return processSubmission('updatePrimaryNamedInsured',[quoteID,contactPublicId], additionalHeaders);
    }
    /* Update the primary named insured
     *
     * @param {Object} quoteID quoteID
     * @param {Object} contactPublicId  is the Contact Public ID
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static createNewAccountContact(data, accountNumber, additionalHeaders = {}) {
        return processSubmission('createNewAccountContact',[data,accountNumber], additionalHeaders);
    }

    static getPaymentInstrumentList_EY(submissionDTO, additionalHeaders = {}) {
        return processSubmission('getBiilingInstrument_EY',[submissionDTO.quoteID, submissionDTO.sessionUUID], additionalHeaders);
    }

    static getURL_EY(submissionDTO, mode, additionalHeaders = {}) {
        return processSubmission('getURL_EY',[submissionDTO.quoteID, submissionDTO.sessionUUID, mode], additionalHeaders);
    }

    static addCreditPaymentInstrument_EY(submissionDTO, paymentDTO, additionalHeaders = {}) {
        return processSubmission('addCreditCardPaymentInstrument_EY',[submissionDTO.quoteID, submissionDTO.sessionUUID, paymentDTO], additionalHeaders);
    }

    static searchBankInstitute_EY(submissionDTO, instNumber, bankName, additionalHeaders = {}) {
        return processSubmission('searchBankInstitute_EY',[submissionDTO.quoteID, submissionDTO.sessionUUID, instNumber, bankName], additionalHeaders);
    }

    static addPaymentInstrument_EY(submissionDTO, paymentDTO, additionalHeaders = {}) {
        return processSubmission('addPaymentInstrument_EY',[submissionDTO.quoteID, submissionDTO.sessionUUID, paymentDTO], additionalHeaders);
    }

    static getMonerisRequestURL_EY(mode, additionalHeaders = {}) {
        return processSubmission('getMonerisRequestURL_EY',[mode], additionalHeaders);
    }

    static isProducerCodesforFlexFund(producerCode,  additionalHeaders = {}) {
        return processSubmission('isProducerCodesforFlexFund',[producerCode], additionalHeaders);
    }

    static updateApplicableExtensions(quoteID, sessionUUID, additionalHeaders = {}) {
        return processSubmission('updateApplicableExtensions', [quoteID, sessionUUID], additionalHeaders);
    }

    static updateLicenseClassDetails(quoteID, driverPublicID, additionalHeaders = {}) {
        return processSubmission('updateLicenseClassDetails', [quoteID, driverPublicID], additionalHeaders);
    }

}
